import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Home, ArrowLeft, Phone, Mail, Clock, Building2, CheckCircle, Play, X, Download, Share2, Calculator, Image as ImageIcon, Facebook, Twitter, Linkedin, Link2, MessageCircle } from 'lucide-react';
import { projects, companyInfo } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';
import EMICalculator from '../components/EMICalculator';
import ImageGallery from '../components/ImageGallery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projects.find(p => p.id === parseInt(id));
  
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [showEMICalculator, setShowEMICalculator] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
          <button
            onClick={() => navigate('/')}
            className="text-amber-600 hover:text-amber-700 font-semibold"
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ready to Move':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Under Construction':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Launching Soon':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    
    try {
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        project_id: project.id,
        project_name: project.name,
        message: formData.message
      };

      await axios.post(`${BACKEND_URL}/api/inquiries/site-visit`, bookingData);
      toast.success('Thank you! We will contact you shortly to confirm your site visit.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setShowInquiryForm(false);
    } catch (error) {
      toast.error('Failed to book site visit. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDownloadBrochure = () => {
    if (project.brochureUrl && project.brochureUrl !== '#') {
      window.open(project.brochureUrl, '_blank');
      toast.success('Opening brochure...');
    } else {
      toast.info('Brochure will be available soon!');
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Check out ${project.name} - ${project.description}`;
    
    switch(platform) {
      case 'whatsapp':
        const whatsappMessage = `🏠 *${project.name}*\n\n` +
          `📍 Location: ${project.location}\n` +
          `💰 Price: Starting from ₹${project.price}\n` +
          `🏡 Type: ${project.bedrooms}\n` +
          `📐 Area: ${project.area}\n\n` +
          `${project.description}\n\n` +
          `View full details: ${url}\n\n` +
          `Contact KIANA DEVELOPERS for more information!`;
        window.open(`https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
        break;
    }
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-700 hover:text-amber-700 transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back to Projects</span>
          </button>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[500px]">
        <img
          src={project.image}
          alt={project.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Project Title and restored View Button */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-4">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">{project.name}</h1>
            <div className="flex items-center text-white text-lg mb-4">
              <MapPin size={20} className="mr-2" />
              <span>{project.location}</span>
            </div>
            {/*Restored "VIEW ON GOOGLE MAPS" Button */}
            {project.mapLink && (
              <a
                href={project.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white text-amber-600 font-semibold rounded-lg hover:bg-amber-50 transition-colors"
              >
                <MapPin size={20} className="mr-2" />
                View on Google Maps
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">{project.description}</p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                  <Home className="text-amber-600 mb-3" size={32} />
                  <div className="text-sm text-gray-600 mb-1">Configuration</div>
                  <div className="text-xl font-bold text-gray-900">{project.bedrooms}</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                  <Building2 className="text-amber-600 mb-3" size={32} />
                  <div className="text-sm text-gray-600 mb-1">Area</div>
                  <div className="text-xl font-bold text-gray-900">{project.area}</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                  <Clock className="text-amber-600 mb-3" size={32} />
                  <div className="text-sm text-gray-600 mb-1">Timeline</div>
                  <div className="text-sm font-bold text-gray-900">{project.timeline}</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50 rounded-2xl p-6 shadow-lg border border-amber-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => setShowGallery(true)}
                  className="flex flex-col items-center justify-center space-y-2 p-4 bg-white hover:bg-amber-50 rounded-xl border border-gray-200 hover:border-amber-300 transition-all duration-300 transform hover:scale-105"
                >
                  <ImageIcon className="text-amber-600" size={28} />
                  <span className="text-sm font-semibold text-gray-700">View Gallery</span>
                </button>

                <button
                  onClick={() => setShowEMICalculator(true)}
                  className="flex flex-col items-center justify-center space-y-2 p-4 bg-white hover:bg-amber-50 rounded-xl border border-gray-200 hover:border-amber-300 transition-all duration-300 transform hover:scale-105"
                >
                  <Calculator className="text-amber-600" size={28} />
                  <span className="text-sm font-semibold text-gray-700">EMI Calculator</span>
                </button>

                <button
                  onClick={handleDownloadBrochure}
                  className="flex flex-col items-center justify-center space-y-2 p-4 bg-white hover:bg-amber-50 rounded-xl border border-gray-200 hover:border-amber-300 transition-all duration-300 transform hover:scale-105"
                >
                  <Download className="text-amber-600" size={28} />
                  <span className="text-sm font-semibold text-gray-700">Download</span>
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="w-full flex flex-col items-center justify-center space-y-2 p-4 bg-white hover:bg-amber-50 rounded-xl border border-gray-200 hover:border-amber-300 transition-all duration-300 transform hover:scale-105"
                  >
                    <Share2 className="text-amber-600" size={28} />
                    <span className="text-sm font-semibold text-gray-700">Share</span>
                  </button>

                  {showShareMenu && (
                    <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-10 min-w-[200px]">
                      <div className="space-y-2">
                        <button onClick={() => handleShare('whatsapp')} className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-green-50 rounded-lg transition-colors text-left">
                          <MessageCircle size={20} className="text-green-600" />
                          <span className="text-gray-700">WhatsApp</span>
                        </button>
                        <button onClick={() => handleShare('facebook')} className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left">
                          <Facebook size={20} className="text-blue-600" />
                          <span className="text-gray-700">Facebook</span>
                        </button>
                        <button onClick={() => handleShare('twitter')} className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-sky-50 rounded-lg transition-colors text-left">
                          <Twitter size={20} className="text-sky-500" />
                          <span className="text-gray-700">Twitter</span>
                        </button>
                        <button onClick={() => handleShare('linkedin')} className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left">
                          <Linkedin size={20} className="text-blue-700" />
                          <span className="text-gray-700">LinkedIn</span>
                        </button>
                        <button onClick={() => handleShare('copy')} className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left">
                          <Link2 size={20} className="text-gray-600" />
                          <span className="text-gray-700">Copy Link</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Specifications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(project.specifications).map(([key, value]) => (
                  <div key={key} className="flex items-start space-x-3">
                    <CheckCircle className="text-amber-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <div className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                      <div className="font-semibold text-gray-900">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Premium Amenities</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {project.amenities.map((amenity) => (
                  <div key={amenity} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-amber-50 transition-colors">
                    <CheckCircle className="text-amber-600 flex-shrink-0" size={20} />
                    <span className="text-gray-700 font-medium">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Note: Embedded Iframe Location Map removed from here */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-8 shadow-xl text-white sticky top-24">
              <div className="text-sm mb-2 opacity-90">Starting From</div>
              <div className="text-4xl font-bold mb-6">{project.price.split(' - ')[0]}</div>
              <button onClick={() => setShowInquiryForm(true)} className="w-full bg-white text-amber-700 hover:bg-gray-100 py-4 rounded-lg font-semibold mb-3 transition-all duration-300 transform hover:scale-105">
                Book a Site Visit
              </button>
              <a href={`tel:${companyInfo.contact.phone}`} className="w-full bg-amber-800 hover:bg-amber-900 text-white py-4 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-105">
                <Phone size={20} />
                <span>Call Now</span>
              </a>
              <div className="mt-6 pt-6 border-t border-white/20">
                <div className="text-sm opacity-90 mb-2">Need more information?</div>
                <a href={`mailto:${companyInfo.contact.email}`} className="text-white hover:text-amber-100 font-medium flex items-center space-x-2">
                  <Mail size={16} />
                  <span className="text-sm">Email Us</span>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="relative h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center mb-4 overflow-hidden group cursor-pointer">
                <img src={project.image} alt="Virtual Tour" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="text-amber-600 ml-1" size={28} />
                  </div>
                </div>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Virtual Tour Available</h3>
              <p className="text-sm text-gray-600">Experience the property from the comfort of your home</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4">Quick Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Type</span>
                  <span className="font-semibold text-gray-900">Residential</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Status</span>
                  <span className="font-semibold text-gray-900">{project.status}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Possession</span>
                  <span className="font-semibold text-gray-900">{project.timeline.replace('Expected Completion: ', '').replace('Launch Date: ', '').replace('Ready for Immediate Possession', 'Immediate')}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">RERA Approved</span>
                  <span className="font-semibold text-green-600">Yes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals remain unchanged */}
      {showInquiryForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-scale-in">
            <button onClick={() => setShowInquiryForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
              <X size={24} />
            </button>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Book a Site Visit</h3>
            <form onSubmit={handleInquirySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none" placeholder="Your name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none" placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none" placeholder="+1 (555) 000-0000" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows="3" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none resize-none" placeholder="Any specific requirements..."></textarea>
              </div>
              <button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105">
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}
      <EMICalculator isOpen={showEMICalculator} onClose={() => setShowEMICalculator(false)} />
      <ImageGallery images={project.gallery || [project.image]} projectName={project.name} isOpen={showGallery} onClose={() => setShowGallery(false)} />
    </div>
  );
};

export default ProjectDetail;